import { utc } from 'moment';
import { TrialService } from '@/services/undergrad/pegasus-silver/trial.service';

const { max } = Math;

interface TrialData {
  active: boolean;
  alreadyUsedTrial: boolean;
  tracking: boolean;
  clientTime: number;
  serverTimeToEnd: number;
  profileId?: number;
  email?: string;
  courseId?: string;
}

export default class Trial {
  private static trialData: TrialData;

  private static async loadTrialData(email: string | null, attempts = 0) {
    if (this.trialData === undefined) {
      const params = window.location.search
        .substring(1)
        .split('&')
        .map((e) => e.split('='));
      const trialViewParam = params.find(([key]) => key === 'trial-view');

      if (trialViewParam) {
        this.trialData = {
          active: true,
          alreadyUsedTrial: true,
          tracking: !!params.find(([key]) => key === 'tracking'),
          clientTime: new Date().getTime(),
          serverTimeToEnd: (parseFloat(trialViewParam[1]) || 15) * 8.64e7,
          profileId: 0,
        };

        return;
      }

      if (email) {
        try {
          const trialUser = await new TrialService().getTrialUser(email);
          if (!trialUser && attempts < 3) {
            await this.loadTrialData(email, attempts + 1);
            return;
          }

          if (trialUser) {
            const trialEndDate = new Date(trialUser.trialEndDate);
            const currentServerDate = new Date(trialUser.currentServerDate);
            this.trialData = {
              active: trialUser.isActive,
              alreadyUsedTrial: !!trialUser.email,
              tracking: true,
              clientTime: new Date().getTime(),
              serverTimeToEnd:
                utc(trialUser.isActive ? trialEndDate : undefined).valueOf() -
                utc(currentServerDate).valueOf(),
              profileId: trialUser.profileId,
              email: trialUser.email,
              courseId: trialUser.courseId,
            };
          } else {
            this.trialData = {
              active: false,
              alreadyUsedTrial: false,
              tracking: false,
              clientTime: new Date().getTime(),
              serverTimeToEnd: 0,
              profileId: 0,
            };
          }
        } catch (error) {
          this.trialData = {
            active: false,
            alreadyUsedTrial: false,
            tracking: false,
            clientTime: new Date().getTime(),
            serverTimeToEnd: 0,
            profileId: 0,
          };
        }
      }
    }
  }

  static async isInTrial(email: string | null): Promise<boolean> {
    if (!this.trialData) await this.loadTrialData(email);

    return this.trialData?.active;
  }

  static async isInTrialTracking(): Promise<boolean> {
    return this.trialData?.tracking;
  }

  static async isUGTrial(): Promise<boolean> {
    return !!this.trialData?.profileId;
  }

  static async hasAlreadyUsedTrial(email: string | null): Promise<boolean> {
    await this.loadTrialData(email);

    return this.trialData?.alreadyUsedTrial;
  }

  static async getTimeToEndOfTrial(): Promise<number> {
    await this.loadTrialData(this.trialData?.email ?? '');
    if (this.trialData) {
      const { clientTime, serverTimeToEnd } = this.trialData;
      const timeSinceRequestToServer = max(0, new Date().getTime() - clientTime);

      return max(0, serverTimeToEnd - timeSinceRequestToServer);
    }

    return 0;
  }

  static async finishTrialAndRemoveAccess(attempts = 0): Promise<void> {
    if (
      this.trialData &&
      this.trialData.email &&
      this.trialData.courseId &&
      this.trialData.profileId
    ) {
      const success = await new TrialService().removeUserAccess(
        this.trialData.email,
        this.trialData.courseId,
        this.trialData.profileId,
      );
      if (!success && attempts < 3) await this.finishTrialAndRemoveAccess(attempts + 1);
    }
  }
}
